import React, { useEffect } from "react";
import { Box } from "./Box";
import { Button } from "./Button";

export interface DrawerProps {
 isOpen: boolean;
 onClose: () => void;
 title?: string;
 children: React.ReactNode;
 position?: "left" | "right" | "top" | "bottom";
 size?: "sm" | "md" | "lg" | "xl" | "full";
 className?: string;
}

const positionClasses = {
 left: "left-0 top-0 bottom-0",
 right: "right-0 top-0 bottom-0",
 top: "top-0 left-0 right-0",
 bottom: "bottom-0 left-0 right-0",
};

const sizeClasses = {
 sm: "w-64",
 md: "w-96",
 lg: "w-[32rem]",
 xl: "w-[40rem]",
 full: "w-full",
};

const sizeClassesVertical = {
 sm: "h-64",
 md: "h-96",
 lg: "h-[32rem]",
 xl: "h-[40rem]",
 full: "h-full",
};

export const Drawer: React.FC<DrawerProps> = ({
 isOpen,
 onClose,
 title,
 children,
 position = "right",
 size = "md",
 className = "",
}) => {
 useEffect(() => {
  if (isOpen) {
   document.body.style.overflow = "hidden";
  } else {
   document.body.style.overflow = "";
  }
  return () => {
   document.body.style.overflow = "";
  };
 }, [isOpen]);

 if (!isOpen) return null;

 const isVertical = position === "top" || position === "bottom";
 const sizeClass = isVertical ? sizeClassesVertical[size] : sizeClasses[size];

 return (
  <>
   <div
    className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
    onClick={onClose}
    aria-hidden="true"
   />
   <Box
    className={`
     fixed z-50 
     ${className.includes("bg-") ? "" : "bg-white"}
     ${positionClasses[position]}
     ${sizeClass}
     ${isVertical ? "overflow-y-auto" : "overflow-x-hidden overflow-y-auto"}
     ${className}
    `}
    role="dialog"
    aria-modal="true"
    aria-labelledby={title ? "drawer-title" : undefined}
   >
    {title && (
     <div className="flex items-center justify-between p-4 border-b border-gray-200">
      <h2 id="drawer-title" className="text-lg font-semibold text-gray-900">
       {title}
      </h2>
      <Button variant="ghost" size="sm" onClick={onClose}>
       <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
       </svg>
      </Button>
     </div>
    )}
    <div className="p-4">{children}</div>
   </Box>
  </>
 );
};

export default Drawer;

